'use client';

import {useState} from 'react';

import type {FormValues, ManagedShippingProps as Props} from './typings';

import useManagedShipping from '@/hooks/use-managed-shipping/use-managed-shipping';
import Modal from '@/molecules/Modal/Modal';
import {pushGAManagedShippingEvent} from '@/utils/gtm';
import DisplayShipping from './DisplayShipping';
import ModalFocusManagement from './ModalFocusManagement';
import ShippingQuoteResult from './ShippingQuoteResult';
import ShippingQuoteSubmitForm from './ShippingQuoteSubmitForm';
import {StyledModalBodyContainer, StyledModalTitle} from './styles';
import {getInitialDestinationCountry, getManagedShippingProps} from './utils';

/**
 * @description Managed Shipping component to fetch and display shipping estimates.
 * @param {Props} props - Props for the ManagedShipping component.
 * @param {string} props.selectedCurrency - The user selected currency code.
 * @param {Object} props.selectedProduct - The currently selected product object.
 * @param {string} props.categorySlug - The category slug for the product (e.g., 'painting-oil').
 * @param {Object|null} props.initialShippingInfo - Initial shipping information if available.
 * @param {string} props.productType - The type of the product (e.g., 'original' or 'prints').
 * @param {boolean} props.isEnabled - Flag to enable or disable the managed shipping feature.
 * @returns {JSX.Element|null} The ManagedShipping component or `null` if not enabled.
 * - `null` is returned when the managed shipping feature is disabled,
 * but the hook logic remains mounted for caching purposes.
 */
const ManagedShipping = ({
  selectedCurrency,
  selectedProduct,
  categorySlug,
  initialShippingInfo,
  productType,
  isEnabled: isFeatureEnabled,
}: Props): JSX.Element | null => {
  const {origin, objects, currency, productSlug, isEnabled, quoteTypes} = getManagedShippingProps({
    selectedCurrency,
    selectedProduct,
    categorySlug,
    initialShippingInfo,
    isEnabled: isFeatureEnabled,
  });
  const {estimate, error, status, fetchEstimate} = useManagedShipping({
    origin,
    objects,
    currency,
    productSlug,
    isEnabled,
    quoteTypes,
  });
  const [step, setStep] = useState<1 | 2 | null>(null);

  const onSubmit = async (formValues: FormValues) => {
    await fetchEstimate(formValues);
    setStep(2);
  };

  const handleStartEstimate = () => {
    setStep(1);
    pushGAManagedShippingEvent({
      event: 'managed_shipping_modal_click',
      location: 'PDP',
    });
  };

  /**
   * Render no JSX if not enabled
   * But keep the hook logic mounted to manage/revalidate caching
   */
  if (!isEnabled) {
    return null;
  }

  return (
    <>
      <DisplayShipping
        estimate={estimate}
        productSlug={selectedProduct.slug}
        initialShippingInfo={initialShippingInfo}
        productType={productType}
        handleStartEstimate={handleStartEstimate}
      />

      {step !== null && (
        <ModalFocusManagement onClose={() => setStep(null)}>
          <Modal onClose={() => setStep(null)}>
            <StyledModalBodyContainer $isLoading={status === 'loading'}>
              <StyledModalTitle>
                <p>Estimate Shipping Cost</p>
              </StyledModalTitle>
              {step === 1 && (
                <ShippingQuoteSubmitForm
                  onSubmit={onSubmit}
                  isDisabled={status === 'loading'}
                  origin={origin}
                  initialCountry={getInitialDestinationCountry({
                    initialShippingInfo,
                    selectedProduct,
                  })}
                />
              )}
              {step === 2 && (
                <ShippingQuoteResult
                  estimate={estimate}
                  origin={origin}
                  error={error}
                  handleStepBack={() => setStep(1)}
                />
              )}
            </StyledModalBodyContainer>
          </Modal>
        </ModalFocusManagement>
      )}
    </>
  );
};

export default ManagedShipping;
